/**
 * Deterministic, serializable RNG (sfc32) with string seeding.
 * The sim must never touch Math.random or Date.now — every stream is
 * seeded from the save seed so identical saves replay identically.
 */
export type RngState = [number, number, number, number];

/** splitmix32-style string hash producing four 32-bit seeds. */
export function seedFromString(str: string): RngState {
  let h = 0x9e3779b9 ^ str.length;
  const out: number[] = [];
  for (let round = 0; round < 4; round++) {
    for (let i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 0x85ebca6b);
      h = (h << 13) | (h >>> 19);
    }
    h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
    h ^= h >>> 15;
    out.push(h >>> 0);
    h = (h + 0x9e3779b9) | 0;
  }
  return out as unknown as RngState;
}

export class Rng {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  constructor(state: RngState) {
    [this.s0, this.s1, this.s2, this.s3] = state;
    // warm up — avoids low-entropy first draws from sparse seeds
    for (let i = 0; i < 12; i++) this.next();
  }

  static fromString(seed: string): Rng {
    return new Rng(seedFromString(seed));
  }

  /** Derive an independent stream (per subsystem) from this seed. */
  fork(label: string): Rng {
    const [a, b, c, d] = seedFromString(label);
    return new Rng([this.s0 ^ a, this.s1 ^ b, this.s2 ^ c, this.s3 ^ d]);
  }

  getState(): RngState {
    return [this.s0 >>> 0, this.s1 >>> 0, this.s2 >>> 0, this.s3 >>> 0];
  }

  setState(state: RngState): void {
    [this.s0, this.s1, this.s2, this.s3] = state;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    // sfc32
    const t = (((this.s0 + this.s1) | 0) + this.s3) | 0;
    this.s3 = (this.s3 + 1) | 0;
    this.s0 = this.s1 ^ (this.s1 >>> 9);
    this.s1 = (this.s2 + (this.s2 << 3)) | 0;
    this.s2 = (this.s2 << 21) | (this.s2 >>> 11);
    this.s2 = (this.s2 + t) | 0;
    return (t >>> 0) / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    return (this.next() * n) | 0;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  pick<T>(arr: readonly T[]): T {
    return arr[this.int(arr.length)] as T;
  }
}

/** Cheap deterministic 2D hash noise in [0,1) — for terrain, not the sim. */
export function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ (seed | 0);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Bilinear value noise over hash2. */
export function valueNoise2(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const sx = xf * xf * (3 - 2 * xf);
  const sy = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
